import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useModal } from '@/contexts/ModalContext';
import { Icon } from '~/global/components/icon/Icon';
import { useRightSidebar } from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';
import { RightSidebarDrawer } from '~/layout/components/right-sidebar/RightSidebarDrawer';

export interface RightSidebarTriggerProps {
  /** Icon size (chat header uses 24, page headers 20 by default). */
  size?: number;
  /** Extra classes on the pressable. */
  className?: string;
  /** Accessibility label; defaults to a generic "open panel". */
  accessibilityLabel?: string;
}

/**
 * The generic vertical-ellipsis button that opens {@link RightSidebar.Drawer} on
 * mobile. Shared by every screen's header (chat / meal / workout) so the trigger
 * is defined once. Renders nothing when the current screen registered no rail
 * (e.g. workout create mode), so callers don't repeat that guard.
 *
 * Reads the rail here — inside `RightSidebarLayoutProvider` — and hands the tabs
 * to the drawer as props, because the drawer renders at the app root (above that
 * provider) where `useRightSidebar()` is out of scope.
 */
export const RightSidebarTrigger = ({
  size = 20,
  className,
  accessibilityLabel,
}: RightSidebarTriggerProps) => {
  const { t } = useTranslation('common');
  const { showModal } = useModal();
  const { tabs, activeKey, hasRail } = useRightSidebar();

  if (!hasRail || !tabs) {
    return null;
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? t('sidebar.open')}
      onPress={() =>
        showModal(RightSidebarDrawer, { tabs, initialKey: activeKey })
      }
      className={cn(
        'h-10 w-10 items-center justify-center rounded-full active:opacity-70',
        className,
      )}
    >
      <Icon name="EllipsisVertical" size={size} className="text-text-primary" />
    </Pressable>
  );
};
